import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, Star, MessageSquare } from "lucide-react";
import { Button } from "./Button";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { createReview } from "../../redux/Slices/reviewSlice";
import { CreateReviewDto, Milestone, ReviewStatusEnum } from "../../modules/shared/model";
import { GlobalService } from "../../modules";
import StarRating from "./StarRating";

interface ClientReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  milestoneId: string;
  professionalId: string;
  contractId: string;
  onReviewSubmitted?: () => void;
  milestone: any;
}

const ClientReviewModal: React.FC<ClientReviewModalProps> = ({
  isOpen,
  onClose,
  milestoneId,
  professionalId,
  contractId,
  onReviewSubmitted,
  milestone
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { loading } = useAppSelector((state) => state.reviews);

  const [ratings, setRatings] = useState({
    qualityOfService: 0,
    professionalism: 0,
    punctuality: 0,
    communication: 0,
    qualityPriceRatio: 0,
    overallExperience: 0
  });

  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ratingCriteria = [
    {
      key: "qualityOfService" as keyof typeof ratings,
      title: t("clientReviewModal.criteria.qualityOfService.title"),
      description: t("clientReviewModal.criteria.qualityOfService.description"),
      required: true
    },
    {
      key: "professionalism" as keyof typeof ratings,
      title: t("clientReviewModal.criteria.professionalism.title"),
      description: t("clientReviewModal.criteria.professionalism.description"),
      required: true
    },
    {
      key: "punctuality" as keyof typeof ratings,
      title: t("clientReviewModal.criteria.punctuality.title"),
      description: t("clientReviewModal.criteria.punctuality.description"),
      required: true
    },
    {
      key: "communication" as keyof typeof ratings,
      title: t("clientReviewModal.criteria.communication.title"),
      description: t("clientReviewModal.criteria.communication.description"),
      required: true
    },
    {
      key: "qualityPriceRatio" as keyof typeof ratings,
      title: t("clientReviewModal.criteria.qualityPriceRatio.title"),
      description: t("clientReviewModal.criteria.qualityPriceRatio.description"),
      required: true
    },
    {
      key: "overallExperience" as keyof typeof ratings,
      title: t("clientReviewModal.criteria.overallExperience.title"),
      description: t("clientReviewModal.criteria.overallExperience.description"),
      required: true
    }
  ];

  const handleRatingChange = (key: keyof typeof ratings, rating: number) => {
    setRatings(prev => ({
      ...prev,
      [key]: rating
    }));
  };

  const isFormValid = () => {
    return Object.values(ratings).every(rating => rating > 0);
  };

  const calculateOverallRating = () => {
    const values = Object.values(ratings);
    return parseFloat((values.reduce((sum, rating) => sum + rating, 0) / values.length).toFixed(1));
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      GlobalService.showToast("error", t("toast.titles.error"), t("clientReviewModal.rateAllCriteria"));
      return;
    }

    if (!user?.id) {
      GlobalService.showToast("error", t("toast.titles.error"), t("clientReviewModal.userNotFound"));
      return;
    }

    setIsSubmitting(true);

    try {
      const reviewData: CreateReviewDto = {
        milestone: { id: milestoneId },
        author: { id: user.id },
        user: { id: professionalId },
        status: { id: ReviewStatusEnum.Active },
        rating: calculateOverallRating(),
        text: comment.trim() || null,
        qualityOfService: ratings.qualityOfService,
        professionalism: ratings.professionalism,
        punctuality: ratings.punctuality,
        communication: ratings.communication,
        qualityPriceRatio: ratings.qualityPriceRatio,
        overallExperience: ratings.overallExperience,
        canReply: false,
        reply: "",
        helpful: 0,
        notHelpful: 0,
        shares: 0,
        views: 0
      };

      await dispatch(createReview(reviewData)).unwrap();

      GlobalService.showToast("success", t("toast.titles.success"), t("clientReviewModal.reviewSubmittedSuccess"));

      // Reset form
      setRatings({
        qualityOfService: 0,
        professionalism: 0,
        punctuality: 0,
        communication: 0,
        qualityPriceRatio: 0,
        overallExperience: 0
      });
      setComment("");

      onReviewSubmitted?.();
      onClose();
    } catch (error: any) {
      console.error("Failed to submit review:", error);
      GlobalService.showToast("error", "Error", error?.message || t("clientReviewModal.reviewSubmitFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-[10003] flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t("clientReviewModal.title")}</h2>
              <p className="text-gray-600 mt-1">{t("clientReviewModal.subtitle")}</p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {ratingCriteria.map((criterion, index) => (
              <div key={criterion.key} className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {index + 1}. {criterion.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {criterion.description}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <StarRating
                    rating={ratings[criterion.key]}
                    onRatingChange={(rating) => handleRatingChange(criterion.key, rating)}
                    disabled={isSubmitting}
                    size="lg"
                  />
                  <span className="text-sm text-gray-500">
                    {ratings[criterion.key] > 0 ? `${ratings[criterion.key]} ${ratings[criterion.key] !== 1 ? t("clientReviewModal.starsPlural") : t("clientReviewModal.stars")}` : t("clientReviewModal.notRated")}
                  </span>
                </div>
              </div>
            ))}

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-gray-500" />
                <h3 className="text-lg font-semibold text-gray-900">{t("clientReviewModal.additionalComments")}</h3>
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("clientReviewModal.additionalCommentsPlaceholder")}
                className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={isSubmitting}
              />
              <p className="text-xs text-gray-500">
                {t("clientReviewModal.additionalCommentsHelper")}
              </p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Star className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-blue-900">{t("clientReviewModal.overallRating")}</span>
              </div>
              <div className="flex items-center gap-3">
                <StarRating
                  rating={calculateOverallRating()}
                  onRatingChange={() => { }} // Read-only
                  disabled={true}
                  size="lg"
                />
                <span className="text-sm text-blue-700">
                  {calculateOverallRating() > 0 ? `${calculateOverallRating()}/5` : t("clientReviewModal.completeAllRatings")}
                </span>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-white border-t p-6 flex justify-end gap-3">
            <Button
              onClick={handleClose}
              variant="outline"
              disabled={isSubmitting}
            >
              {t("clientReviewModal.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? t("clientReviewModal.submitting") : t("clientReviewModal.submitReview")}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ClientReviewModal;
